import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import useTrackEvent from '../../utils/trackEvent';
import { usePreferences } from '../../context/PreferencesContext';
import ThemesNew from '../../components/Section/themesNew';
import QuestionsNew from '../../components/Section/questionsNew';
import SubmissionNew from '../../components/Section/submissionNew';
import { compressToEncodedURIComponent } from 'lz-string';
import type { Question, Answer } from '../../types/questionnaire';

type TreeNode = Question | Answer;

function Form() {
  const { resetQuestionnaire, setResetQuestionnaire } = usePreferences();

  const getQuestionnaire = () => window.customer.questionnaireData!;
  const trackEvent = useTrackEvent();
  const navigateTo = useNavigate();
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [rootQuestion, setRootQuestion] = useState<Question>(getQuestionnaire());
  const [selectedThemes, setSelectedThemes] = useState<Answer[]>([]);
  const [wrappedThemes, setWrappedThemes] = useState<Answer[]>([]);

  useEffect(() => {
    if (resetQuestionnaire) {
      setResetQuestionnaire(false);
      for (const theme of rootQuestion.answers) {
        uncheckAllChildren(theme);
      }
    }
  }, [resetQuestionnaire]);

  useEffect(() => {
    const hasChecked = rootQuestion.answers.some((theme) => theme.checked);
    if (!hasChecked) setSelectedThemes([]);
  }, [rootQuestion]);

  const uncheckAllChildren = (node: TreeNode) => {
    if ('answers' in node) {
      for (const subQuestion of node.answers) {
        uncheckAllChildren(subQuestion);
      }
    } else if ('questions' in node && node.questions) {
      node.checked = false;
      for (const subQuestion of node.questions) {
        uncheckAllChildren(subQuestion);
      }
    }
  };

  const toggleCallback = (answer: Answer, answerId: number): boolean => {
    if (answer.id === answerId) {
      answer.checked = !answer.checked;
      if (!answer.checked) uncheckAllChildren(answer);
      return true;
    }
    return false;
  };

  const recursiveApply = (question: Question | null | undefined, callback: (answer: Answer, answerId: number) => boolean, answerId: number) => {
    if (question && question.answers) {
      for (const answer of question.answers) {
        if (callback(answer, answerId)) return;
        if (answer.questions) {
          for (const subQuestion of answer.questions) {
            recursiveApply(subQuestion, callback, answerId);
          }
        }
      }
    }
  };

  interface CheckedAnswerEntry { title: string; theme?: string; result?: string; id: number }

  const getCheckedAnswers = (data: Answer[]): CheckedAnswerEntry[] => {
    const checkedAnswers: CheckedAnswerEntry[] = [];
    const traverse = (node: TreeNode) => {
      if ('checked' in node && node.checked) {
        checkedAnswers.push({ title: node.title, theme: node.theme, result: node.result, id: node.id });
      }
      if ('questions' in node && node.questions) node.questions.forEach(traverse);
      if ('answers' in node) node.answers.forEach(traverse);
    };
    data.forEach(traverse);
    return checkedAnswers;
  };

  const toggle = (answerId: number) => {
    setRootQuestion((oldRootQuestion) => {
      const newRootQuestion = { ...oldRootQuestion };
      recursiveApply(newRootQuestion, toggleCallback, answerId);
      return newRootQuestion;
    });
  };

  const falseCallback = (answer: Answer): boolean => { answer.checked = false; return false; };

  const checkCallback = (answer: Answer): boolean => {
    if (!answer.checked && answer.questions) {
      for (const subQuestion of answer.questions) {
        recursiveApply(subQuestion, falseCallback, 0);
      }
    }
    return false;
  };

  interface ThemeAdvice { theme: string; advice: { products: string[]; content: string[] } }

  const createResponse = (question: Question | null | undefined, response: ThemeAdvice[]) => {
    if (question && question.answers) {
      question.answers.forEach((answer) => {
        if (
          answer.checked &&
          ((answer.products && answer.products.length > 0) || (answer.content && answer.content.length > 0)) &&
          answer.theme !== null
        ) {
          let existingTheme = response.find((item) => item.theme === answer.theme);
          if (!existingTheme) {
            existingTheme = { theme: answer.theme!, advice: { products: [], content: [] } };
            response.push(existingTheme);
          }
          if (answer.products) {
            existingTheme.advice.products = [...new Set([...existingTheme.advice.products, ...answer.products])];
          }
          if (answer.content) {
            existingTheme.advice.content = [...new Set([...existingTheme.advice.content, ...answer.content])];
          }
        }
        if (answer.questions) {
          answer.questions.forEach((subQuestion) => createResponse(subQuestion, response));
        }
      });
    }
  };

  const submitAndGoToResultPage = () => {
    const results = { ...rootQuestion };
    recursiveApply(results, checkCallback, 0);
    const response: ThemeAdvice[] = [];
    createResponse(results, response);
    trackEvent('Click_RequestAdvice', {});
    const groupedData = getCheckedAnswers(selectedThemes).reduce<Record<string, { theme: string; subthemes: Record<string, string | undefined>[] }>>((acc, item) => {
      if (!item.theme) return acc;
      if (!acc[item.theme]) acc[item.theme] = { theme: item.theme, subthemes: [] };
      const title = String(item.id);
      const subtheme = acc[item.theme].subthemes.find((sub) => sub[title] !== undefined);
      if (subtheme) {
        subtheme[title] = item.result;
      } else {
        acc[item.theme].subthemes.push({ [title]: item.result });
      }
      return acc;
    }, {});
    const encodeResponseToParams = (response: ThemeAdvice[]) =>
      response.map((item, index) => {
        const themeParam = `theme${index}=${encodeURIComponent(item.theme)}`;
        const productsParam = `products${index}=${encodeURIComponent(item.advice.products.join(','))}`;
        const contentParam = `content${index}=${encodeURIComponent(item.advice.content.join(','))}`;
        return `${themeParam}&${productsParam}&${contentParam}`;
      }).join('&');
    const urlParams = encodeResponseToParams(response);
    const compressedParams = compressToEncodedURIComponent(
      urlParams + '&groupedAnswers=' + encodeURIComponent(JSON.stringify(groupedData))
    );
    navigateTo(`/results?data=${compressedParams}`);
  };

  const addRef = (element: HTMLDivElement | null) => {
    if (element && !sectionRefs.current.includes(element)) {
      sectionRefs.current.push(element);
    }
  };

  const scrollToSection = (index: number) => {
    sectionRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submitThemes = () => {
    const selected = rootQuestion.answers.filter((theme) => theme.checked);
    setSelectedThemes(selected);
    setTimeout(() => { scrollToSection(0); }, 100);
  };

  return (
    <div className="background-header min-h-[calc(100vh-60px)]">
      <div className="site-container">
        <div className="sections space-y-5 lg:space-y-6">
          <Link to="/" className="inline-flex items-center gap-2.5">
            <button className="btn btn-outline-white">
              <i className="fas fa-chevron-left"></i>
              <span className="text-lg">Terug naar startpagina</span>
            </button>
          </Link>
          <h1 className="text-white text-4xl">Vragenlijst</h1>
          <ThemesNew
            themes={rootQuestion}
            onChecked={toggle}
            selectedThemes={selectedThemes}
            wrappedThemes={wrappedThemes}
            setWrappedThemes={setWrappedThemes}
            submitThemes={submitThemes}
          />
          {rootQuestion.answers.map((theme) => (
            <div key={theme.id} ref={(el) => addRef(el)}>
              <QuestionsNew
                theme={theme}
                onChecked={toggle}
                selectedThemes={selectedThemes}
                wrappedThemes={wrappedThemes}
                setWrappedThemes={setWrappedThemes}
              />
            </div>
          ))}
          <SubmissionNew
            themes={rootQuestion}
            onSubmit={submitAndGoToResultPage}
            selectedThemes={selectedThemes}
            isLoading={false}
            error={null}
          />
        </div>
      </div>
    </div>
  );
}

export default Form;
